import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';

type PlannerFieldType = 'text' | 'number' | 'currency';

interface PlannerSliderConfig {
  key: string;
  label: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  editable?: boolean;
  valuePrefix?: string;
  valueSuffix?: string;
  decimals?: number;
  minLabel?: string;
  maxLabel?: string;
  helperText?: string;
  secondaryValue?: unknown;
  secondaryValueKey?: string;
  secondaryPrefix?: string;
  secondarySuffix?: string;
  secondaryDecimals?: number;
}

interface PlannerFieldConfig {
  key: string;
  label: string;
  type?: PlannerFieldType;
  value?: unknown;
  defaultValue?: unknown;
  editable?: boolean;
  placeholder?: string;
  helperText?: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  min?: number;
  max?: number;
}

interface PlannerResultRowConfig {
  label: string;
  value?: unknown;
  valueKey?: string;
  template?: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  tone?: 'neutral' | 'positive' | 'negative' | 'auto';
  positiveColor?: string;
  negativeColor?: string;
  neutralColor?: string;
}

interface PlannerActionButtonConfig {
  label?: string;
  apiUrl?: string;
  method?: string;
  requestPayload?: any;
  queryParams?: Record<string, any>;
  successMessage?: string;
  errorMessage?: string;
  responseMessagePath?: string;
  filterKey?: string | string[];
  filterListener?: string | string[];
  filterListner?: string | string[];
  backgroundColor?: string;
  textColor?: string;
  navigationValue?: string;
}

interface PricePlannerConfig {
  widgetId?: string;
  title?: string;
  subtitle?: string;
  titleAlignment?: 'left' | 'center' | 'right';
  showClose?: boolean;
  filterKey?: string | string[];
  filterListener?: string | string[];
  filterListner?: string | string[];
  sliders?: PlannerSliderConfig[];
  fields?: PlannerFieldConfig[];
  resultRows?: PlannerResultRowConfig[];
  actionButton?: PlannerActionButtonConfig;
  apiUrl?: string;
  method?: string;
  height?: string;
  heightSm?: string;
  heightMd?: string;
  heightLg?: string;
  themeOverrides?: any;
}

@Component({
  selector: 'app-price-planner',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSliderModule],
  templateUrl: './price-planner.component.html',
  styleUrl: './price-planner.component.scss',
})
export class PricePlannerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data?: PricePlannerConfig | null;

  widgetConfig: PricePlannerConfig | null = null;
  sliderConfigs: PlannerSliderConfig[] = [];
  fieldConfigs: PlannerFieldConfig[] = [];
  resultRows: PlannerResultRowConfig[] = [];
  formState: Record<string, any> = {};
  dismissed = false;
  loading = false;
  submitting = false;
  errorMessage = '';
  submitMessage = '';
  submitErrorMessage = '';
  fontScale = 1;

  private readonly configService = inject(ConfigService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private readonly themeService = inject(ThemeService);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly router = inject(Router);
  private readonly globalFilterService = inject(GlobalFilterService);
  private readonly chartFilterHandler = inject(ChartFilterHandlerService);
  private configSub?: Subscription;
  private reflowSub?: Subscription;
  private themeSub?: Subscription;

  @HostBinding('style.--price-planner-font-scale')
  get hostFontScale(): string {
    return this.fontScale.toString();
  }

  ngOnInit(): void {
    this.fontScale = this.responsiveLayoutService.getFontScale();
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.fontScale = this.responsiveLayoutService.getFontScale();
    });

    this.themeSub = this.themeService.theme$.subscribe(() => {
      this.applyResolvedTheme();
    });

    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.initializeComponent();
    }
  }

  ngOnDestroy(): void {
    this.configSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
  }

  get showClose(): boolean {
    return this.widgetConfig?.showClose !== false;
  }

  get titleAlignment(): string {
    return this.widgetConfig?.titleAlignment || 'left';
  }

  get buttonLabel(): string {
    return this.widgetConfig?.actionButton?.label || 'Apply Changes';
  }

  onSliderChange(slider: PlannerSliderConfig, rawValue: unknown): void {
    if (!slider.key) {
      return;
    }

    const nextValue = this.clampNumber(
      this.toFiniteNumber(rawValue, this.formState[slider.key] ?? slider.value ?? slider.defaultValue ?? 0),
      this.toFiniteNumber(slider.min, 0),
      this.toFiniteNumber(slider.max, 100),
    );
    const decimals = this.decimalPlaces(slider.step ?? 1);
    this.formState[slider.key] = Number(nextValue.toFixed(decimals));
  }

  onFieldChange(field: PlannerFieldConfig, rawValue: unknown): void {
    if (!field.key || field.editable === false) {
      return;
    }

    if (field.type === 'number' || field.type === 'currency') {
      const numeric = this.toFiniteNumber(rawValue, this.formState[field.key]);
      const clamped = this.clampNumber(
        numeric,
        field.min === undefined ? Number.NEGATIVE_INFINITY : Number(field.min),
        field.max === undefined ? Number.POSITIVE_INFINITY : Number(field.max),
      );
      const decimals = Number(field.decimals ?? 0);
      this.formState[field.key] = Number.isFinite(clamped)
        ? Number(clamped.toFixed(decimals))
        : this.formState[field.key];
      return;
    }

    this.formState[field.key] = rawValue ?? '';
  }

  dismiss(): void {
    this.dismissed = true;
  }

  submitPlanner(): void {
    const actionButton = this.widgetConfig?.actionButton;
    if (this.submitting) {
      return;
    }

    this.submitMessage = '';
    this.submitErrorMessage = '';

    const resolvedState = this.getResolvedState();
    this.emitFilterPayload(resolvedState, actionButton);

    if (actionButton?.apiUrl) {
      const apiUrl = String(this.resolveDynamicValue(actionButton.apiUrl) || '');
      const method = this.resolveMethod(apiUrl, actionButton.method);
      const requestPayload = this.resolveDynamicValue(
        actionButton.requestPayload ?? {
          state: resolvedState,
          widgetId: this.widgetConfig?.widgetId,
        },
      );
      const queryParams = this.resolveDynamicValue(actionButton.queryParams);

      this.submitting = true;
      this.configService
        .getConfig(apiUrl, actionButton, this.buildRequestFilters(requestPayload, queryParams), method)
        .subscribe({
          next: (response) => {
            const responseMessagePath = String(this.resolveDynamicValue(actionButton.responseMessagePath || '') || '');
            const responseMessage = responseMessagePath ? this.getValueByPath(response, responseMessagePath) : '';
            this.submitting = false;
            this.submitMessage = String(responseMessage || actionButton.successMessage || 'Changes applied successfully.');
            this.submitErrorMessage = '';
            this.runNavigationAction(actionButton);
          },
          error: () => {
            this.submitting = false;
            this.submitMessage = '';
            this.submitErrorMessage = actionButton.errorMessage || 'Unable to apply changes.';
          },
        });
      return;
    }

    this.submitMessage = actionButton?.successMessage || 'Changes applied successfully.';
    this.runNavigationAction(actionButton);
  }

  getSliderDisplayValue(slider: PlannerSliderConfig): string {
    const resolvedState = this.getResolvedState();
    const value = resolvedState[slider.key];
    const primary = this.formatValue(
      value,
      slider.decimals ?? this.decimalPlaces(slider.step ?? 1),
      slider.valuePrefix,
      slider.valueSuffix,
    );
    const secondary = this.getSliderSecondaryDisplay(slider, resolvedState);

    return secondary ? `${primary} / ${secondary}` : primary;
  }

  getSliderMinLabel(slider: PlannerSliderConfig): string {
    if (slider.minLabel) {
      return slider.minLabel;
    }

    return this.formatValue(slider.min ?? 0, 0, '', slider.valueSuffix);
  }

  getSliderMaxLabel(slider: PlannerSliderConfig): string {
    if (slider.maxLabel) {
      return slider.maxLabel;
    }

    return this.formatValue(slider.max ?? 100, 0, '', slider.valueSuffix);
  }

  getFieldInputType(field: PlannerFieldConfig): string {
    return field.type === 'number' || field.type === 'currency' ? 'number' : 'text';
  }

  getFieldDisplayValue(field: PlannerFieldConfig): any {
    const resolvedState = this.getResolvedState();
    const rawValue = resolvedState[field.key];

    if (field.type === 'currency') {
      return this.formatValue(rawValue, field.decimals ?? 0, field.prefix || '$', field.suffix);
    }

    if (field.editable === false && field.type === 'number') {
      return this.formatValue(rawValue, field.decimals ?? 0, field.prefix, field.suffix);
    }

    return rawValue ?? '';
  }

  getEditableFieldValue(field: PlannerFieldConfig): any {
    return this.formState[field.key] ?? '';
  }

  getResultValue(row: PlannerResultRowConfig): string {
    const resolvedState = this.getResolvedState();
    const rawValue = row.template
      ? this.resolveDynamicValue(row.template)
      : row.valueKey
        ? resolvedState[row.valueKey]
        : row.value;

    if (typeof rawValue === 'number') {
      const prefix = row.tone === 'auto' && rawValue > 0 && !row.prefix ? '+' : row.prefix;
      return this.formatValue(rawValue, row.decimals ?? 0, prefix, row.suffix);
    }

    return String(rawValue ?? '');
  }

  getResultStyles(row: PlannerResultRowConfig): Record<string, string> {
    const resolvedState = this.getResolvedState();
    const rawValue = row.valueKey
      ? resolvedState[row.valueKey]
      : row.value;

    const numericValue = Number(rawValue);
    const tone = row.tone || 'neutral';
    let color = row.neutralColor || '#111827';

    if (tone === 'positive') {
      color = row.positiveColor || '#16a34a';
    } else if (tone === 'negative') {
      color = row.negativeColor || '#dc2626';
    } else if (tone === 'auto' && Number.isFinite(numericValue)) {
      if (numericValue > 0) {
        color = row.positiveColor || '#16a34a';
      } else if (numericValue < 0) {
        color = row.negativeColor || '#dc2626';
      }
    }

    return { color };
  }

  getActionButtonStyles(): Record<string, string> {
    const actionButton = this.widgetConfig?.actionButton;
    return {
      background: String(actionButton?.backgroundColor || 'var(--dvm-btn-primary-bg, #dc2626)'),
      color: String(actionButton?.textColor || 'var(--dvm-btn-primary-text, #ffffff)'),
    };
  }

  private initializeComponent(): void {
    this.dismissed = false;
    this.loading = true;
    this.errorMessage = '';
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.widgetConfig = null;
    this.sliderConfigs = [];
    this.fieldConfigs = [];
    this.resultRows = [];
    this.formState = {};
    this.configSub?.unsubscribe();

    if (!this.data) {
      this.loading = false;
      return;
    }

    const configUrl = this.data.apiUrl || '';
    if (!configUrl) {
      this.applyConfig(this.data);
      return;
    }

    this.configSub = this.configService
      .getConfig(configUrl, this.data, undefined, this.resolveMethod(configUrl, this.data.method))
      .subscribe({
        next: (remoteConfig) => this.applyConfig(this.mergePlainObjects(remoteConfig, this.data!)),
        error: () => {
          this.loading = false;
          this.errorMessage = 'Unable to load price planner configuration.';
        },
      });
  }

  private applyConfig(config: PricePlannerConfig): void {
    this.widgetConfig = config;
    this.sliderConfigs = Array.isArray(config.sliders) ? config.sliders : [];
    this.fieldConfigs = Array.isArray(config.fields) ? config.fields : [];
    this.resultRows = Array.isArray(config.resultRows) ? config.resultRows : [];
    this.formState = this.buildInitialState(config);
    this.applyResolvedTheme();
    this.loading = false;
  }

  private buildInitialState(config: PricePlannerConfig): Record<string, any> {
    const nextState: Record<string, any> = {};

    (config.sliders || []).forEach((slider) => {
      if (!slider.key) {
        return;
      }
      const fallback = slider.defaultValue ?? slider.value ?? slider.min ?? 0;
      nextState[slider.key] = this.toFiniteNumber(slider.value ?? slider.defaultValue, Number(fallback));
    });

    (config.fields || []).forEach((field) => {
      if (!field.key) {
        return;
      }

      if (field.type === 'number' || field.type === 'currency') {
        nextState[field.key] = this.toFiniteNumber(field.value ?? field.defaultValue, 0);
        return;
      }

      nextState[field.key] = field.value ?? field.defaultValue ?? '';
    });

    return nextState;
  }

  private getResolvedState(): Record<string, any> {
    return { ...this.formState };
  }

  private getSliderSecondaryDisplay(slider: PlannerSliderConfig, resolvedState: Record<string, any>): string {
    const secondaryRaw = slider.secondaryValueKey
      ? resolvedState[slider.secondaryValueKey]
      : slider.secondaryValue;

    if (secondaryRaw === undefined || secondaryRaw === null || secondaryRaw === '') {
      return '';
    }

    if (typeof secondaryRaw === 'number') {
      return this.formatValue(
        secondaryRaw,
        slider.secondaryDecimals ?? 0,
        slider.secondaryPrefix,
        slider.secondarySuffix,
      );
    }

    return `${slider.secondaryPrefix || ''}${secondaryRaw}${slider.secondarySuffix || ''}`;
  }

  private emitFilterPayload(resolvedState: Record<string, any>, actionButton?: PlannerActionButtonConfig): void {
    const filterKey = actionButton?.filterKey || this.data?.filterKey;
    if (!filterKey) {
      return;
    }

    const listener = actionButton?.filterListener || actionButton?.filterListner || this.data?.filterListener || this.data?.filterListner;
    const payloadKeys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey)
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);
    const payload = payloadKeys.reduce<Record<string, any>>((acc, key) => {
      acc[key] = resolvedState[key];
      return acc;
    }, {});

    this.chartFilterHandler.updateFilterSelection(
      {
        ...this.data,
        filterKey,
        filterListener: listener,
        filterListner: listener,
      },
      payload,
      payload,
    );
  }

  private runNavigationAction(actionButton?: PlannerActionButtonConfig): void {
    const navigationValue = actionButton?.navigationValue || this.buttonLabel;
    executeNavigationFromConfig(this.router, this.data, {
      name: navigationValue,
      value: navigationValue,
      state: this.getResolvedState(),
    });
  }

  private applyResolvedTheme(): void {
    const overrides = this.widgetConfig?.themeOverrides || this.data?.themeOverrides;
    if (!overrides) {
      return;
    }
    const resolvedTheme = this.themeService.resolve(overrides);
    this.themeService.applyThemeTokensTo(resolvedTheme, this.hostElement.nativeElement);
  }

  private mergePlainObjects<T>(base: T | undefined, override: T | undefined): T {
    if (!base) {
      return (override || {}) as T;
    }
    if (!override) {
      return base;
    }

    const merged = { ...(base as Record<string, any>) };
    Object.keys(override as Record<string, any>).forEach((key) => {
      const overrideValue = (override as Record<string, any>)[key];
      const baseValue = (base as Record<string, any>)[key];
      if (
        overrideValue &&
        typeof overrideValue === 'object' &&
        !Array.isArray(overrideValue) &&
        baseValue &&
        typeof baseValue === 'object' &&
        !Array.isArray(baseValue)
      ) {
        merged[key] = this.mergePlainObjects(baseValue, overrideValue);
      } else {
        merged[key] = overrideValue;
      }
    });
    return merged as T;
  }

  private resolveMethod(url: string, explicitMethod?: string): string {
    if (explicitMethod) {
      return explicitMethod;
    }
    return String(url).endsWith('.json') ? 'get' : 'post';
  }

  private buildRequestFilters(requestPayload: any, queryParams: any): any {
    const filters: any = {};

    if (requestPayload !== undefined && requestPayload !== null && requestPayload !== '') {
      filters.requestParams = requestPayload;
    }

    if (queryParams && typeof queryParams === 'object' && !Array.isArray(queryParams)) {
      filters.queryParams = queryParams;
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  private resolveDynamicValue(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.resolveDynamicValue(item));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc: Record<string, any>, key: string) => {
        acc[key] = this.resolveDynamicValue(value[key]);
        return acc;
      }, {});
    }

    if (typeof value !== 'string') {
      return value;
    }

    const exactMatch = value.match(/^\{\{\s*([^}]+)\s*\}\}$/);
    if (exactMatch) {
      return this.resolveDynamicToken(exactMatch[1]);
    }

    return value.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_match, token: string) => {
      const resolved = this.resolveDynamicToken(token);
      return resolved === undefined || resolved === null ? '' : String(resolved);
    });
  }

  private resolveDynamicToken(token: string): any {
    const normalized = token.trim();
    const state = this.getResolvedState();

    if (normalized === 'state') {
      return state;
    }
    if (normalized.startsWith('state.')) {
      return this.getValueByPath(state, normalized.slice(6));
    }
    if (normalized === 'widget') {
      return this.widgetConfig;
    }
    if (normalized.startsWith('widget.')) {
      return this.getValueByPath(this.widgetConfig, normalized.slice(7));
    }
    if (normalized === 'filters') {
      return this.globalFilterService.getCurrentFilters();
    }
    if (normalized.startsWith('filters.')) {
      return this.getValueByPath(this.globalFilterService.getCurrentFilters(), normalized.slice(8));
    }

    return this.getValueByPath(state, normalized);
  }

  private formatValue(
    rawValue: unknown,
    decimals = 0,
    prefix = '',
    suffix = '',
  ): string {
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue)) {
      return `${prefix || ''}${numericValue.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${suffix || ''}`;
    }

    if (rawValue === undefined || rawValue === null) {
      return '';
    }

    return `${prefix || ''}${String(rawValue)}${suffix || ''}`;
  }

  private toFiniteNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private clampNumber(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private decimalPlaces(value: number): number {
    const text = String(value ?? 1);
    return text.includes('.') ? text.split('.')[1].length : 0;
  }

  private getValueByPath(source: any, path: string): any {
    if (!source || !path) {
      return undefined;
    }

    return String(path)
      .split('.')
      .reduce((current: any, key: string) => current?.[key], source);
  }
}
